import { FormMessage, useFormField } from "@/components/ui/form";
import { cn } from "@/lib/utils";

export interface ULThemeFormMessageProps {
  /**
   * SDK error message (takes precedence over form validation errors)
   */
  sdkError?: string;
  /**
   * Whether there is a form validation error
   */
  hasFormError?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

const ULThemeFormMessage = ({ sdkError, hasFormError, className }: ULThemeFormMessageProps) => {
  // Same id FormMessage uses, so the field's aria-describedby also resolves
  // for SDK errors (see ULThemeFloatingLabelField).
  const { formMessageId } = useFormField();

  // Don't render if no errors at all
  if (!sdkError && !hasFormError) {
    return null;
  }

  // Always render with consistent theming for ANY error
  return (
    <div
      className={cn(
        "flex mb-2 items-center",
        "theme-universal:text-(--color-error-text)",
        "theme-universal:font-(weight:--font-weight-error-text)",
        "theme-universal:text-(length:--font-size-error-text)",
        "theme-universal:leading-(height:--line-height-error-text)",
        className,
      )}
      role="alert"
      aria-live="polite"
    >
      {sdkError ? (
        <p
          id={formMessageId}
          className="text-destructive theme-universal:text-(--color-error-text)"
        >
          {sdkError}
        </p>
      ) : (
        <FormMessage
          className={cn(
            "theme-universal:text-(--color-error-text)",
            // The base FormMessage component hardcodes text-sm directly on
            // its own <p>, which wins over inheriting the wrapper's
            // font-size - so this needs to be set here too, not just above.
            "theme-universal:font-(weight:--font-weight-error-text)",
            "theme-universal:text-(length:--font-size-error-text)",
            "theme-universal:leading-(height:--line-height-error-text)",
          )}
        />
      )}
    </div>
  );
};

ULThemeFormMessage.displayName = "ULThemeFormMessage";

export { ULThemeFormMessage };
